import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  FileArrowUp, Trash, ChatText, MicrophoneStage, Stop, PaperPlaneTilt,
  Robot, User as UserIcon, Star, CheckCircle, ChartBar, Lightbulb, Brain,
  ShieldCheck, Lightning, MagnifyingGlass, PencilSimple,
} from "@phosphor-icons/react";
import KbEditDialog from "@/components/KbEditDialog";

/**
 * Admin page: gestisce la Knowledge Base tecnica del Chatbot Pro.
 * Tab 1: Documenti (upload PDF/TXT)
 * Tab 2: Istruzioni (testo diretto)
 * Tab 3: Chat di istruzione (testo + audio via MediaRecorder + Whisper)
 */
export default function ChatbotSettings({ embedded = false }) {
  const [tab, setTab] = useState("documents");
  const [kb, setKb] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadKb = () => api.get("/admin/chatbot-tech/knowledge").then(r => setKb(r.data || []));

  useEffect(() => { loadKb(); }, []);

  const deleteEntry = async (id) => {
    if (!window.confirm("Eliminare questa entry dalla KB tecnica?")) return;
    await api.delete(`/admin/chatbot-tech/knowledge/${id}`);
    toast.success("Eliminato");
    loadKb();
  };

  const toggleActive = async (row) => {
    await api.patch(`/admin/chatbot-tech/knowledge/${row._id || row.id}`, { is_active: !row.is_active });
    loadKb();
  };

  // Iter208.34 — Rimosso `ProChatbotMasterToggle` (interruttore globale).
  // Il Chatbot PRO è ora fuso con l'Agente AI: nessun flag da attivare.
  // Le impostazioni della KB (Documenti/Istruzioni/Chat/Statistiche/Limiti)
  // restano invariate e sono le impostazioni dell'Agente AI a tutti gli effetti.

  return (
    <div>
      {!embedded && (
        <div className="mb-6">
          <div className="label-eyebrow">Amministrazione</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Agente AI — Impostazioni</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Gestisci la knowledge base dell'<strong>Agente AI</strong> (già Chatbot PRO) che risponde ai professionisti sul Marketplace.
            Carica normative (PDF/TXT), aggiungi istruzioni testuali o istruiscilo tramite chat vocale.
          </p>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="chatbot-tech-tabs">
          <TabsTrigger value="documents" data-testid="tab-documents">Documenti ({kb.filter(k => k.kind === "document").length})</TabsTrigger>
          <TabsTrigger value="instructions" data-testid="tab-instructions">Istruzioni ({kb.filter(k => k.kind === "instruction").length})</TabsTrigger>
          <TabsTrigger value="chat" data-testid="tab-chat">Chat di istruzione</TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-stats">Statistiche</TabsTrigger>
          <TabsTrigger value="limits" data-testid="tab-limits">Limiti d'uso</TabsTrigger>
        </TabsList>

        <TabsContent value="documents" className="mt-6">
          <DocumentsTab kb={kb.filter(k => k.kind === "document")} onChange={loadKb} onDelete={deleteEntry} onToggle={toggleActive} />
        </TabsContent>

        <TabsContent value="instructions" className="mt-6">
          <InstructionsTab kb={kb.filter(k => k.kind === "instruction")} onChange={loadKb} onDelete={deleteEntry} onToggle={toggleActive} />
        </TabsContent>

        <TabsContent value="chat" className="mt-6">
          <TrainingChatTab onKbChange={loadKb} />
        </TabsContent>

        <TabsContent value="stats" className="mt-6">
          <StatsTab />
        </TabsContent>

        <TabsContent value="limits" className="mt-6">
          <LimitsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================
// DOCUMENTS TAB — upload PDF/TXT
// ============================================================
function DocumentsTab({ kb, onChange, onDelete, onToggle }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [tags, setTags] = useState("");
  const [priority, setPriority] = useState(0);
  const [uploading, setUploading] = useState(false);

  const upload = async () => {
    if (!file) { toast.error("Seleziona un file PDF o TXT"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      if (title) form.append("title", title);
      if (tags) form.append("tags", tags);
      form.append("priority", String(priority || 0));
      const r = await api.post("/admin/chatbot-tech/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Caricato: ${r.data.chunks_count} chunks estratti`);
      setFile(null); setTitle(""); setTags(""); setPriority(0);
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore upload");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="kpi-card">
        <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
          <FileArrowUp size={20} className="text-purple-700" weight="fill" /> Carica documento (PDF, TXT o Word)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600">File PDF/TXT</label>
            <input
              type="file"
              accept=".pdf,.txt,.md,application/pdf,text/plain"
              onChange={e => setFile(e.target.files?.[0] || null)}
              className="mt-1 block w-full text-sm border border-slate-200 rounded-lg p-2"
              data-testid="chatbot-tech-file-input"
            />
            {file && <div className="text-xs text-slate-500 mt-1">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Titolo (opzionale)</label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Es. DPR 380/2001 - Testo unico edilizia" data-testid="chatbot-tech-title" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Tag (separati da virgola)</label>
            <Input value={tags} onChange={e => setTags(e.target.value)} placeholder="edilizia, sanatoria, dpr" data-testid="chatbot-tech-tags" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Priorità (0-10)</label>
            <Input type="number" min="0" max="10" value={priority} onChange={e => setPriority(Number(e.target.value) || 0)} data-testid="chatbot-tech-priority" />
          </div>
        </div>
        <div className="mt-4">
          <Button onClick={upload} disabled={uploading || !file} className="ht-gradient border-0" data-testid="chatbot-tech-upload-btn">
            {uploading ? "Elaborazione…" : "Carica ed estrai"}
          </Button>
        </div>
      </div>

      <KnowledgeList list={kb} onDelete={onDelete} onToggle={onToggle} onReload={onChange} emptyLabel="Nessun documento caricato" />
    </div>
  );
}

// ============================================================
// INSTRUCTIONS TAB — testuali dirette
// ============================================================
function InstructionsTab({ kb, onChange, onDelete, onToggle }) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [tags, setTags] = useState("");
  const [priority, setPriority] = useState(1);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!content.trim()) { toast.error("Contenuto obbligatorio"); return; }
    setSaving(true);
    try {
      await api.post("/admin/chatbot-tech/instruction", {
        title: title || content.substring(0, 60),
        content, priority,
        tags: tags.split(",").map(t => t.trim()).filter(Boolean),
      });
      toast.success("Istruzione salvata");
      setTitle(""); setContent(""); setTags("");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="kpi-card">
        <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
          <ChatText size={20} className="text-purple-700" weight="fill" /> Aggiungi istruzione testuale
        </h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Titolo</label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Es. Regola APE per immobili ante 1976" data-testid="chatbot-tech-instr-title" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Contenuto (istruzione, normativa, procedura)</label>
            <Textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder="Es. Per gli immobili costruiti prima del 1976, l'APE non è obbligatorio in caso di…"
              rows={6}
              data-testid="chatbot-tech-instr-content"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Tag</label>
              <Input value={tags} onChange={e => setTags(e.target.value)} placeholder="ape, catasto" data-testid="chatbot-tech-instr-tags" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Priorità</label>
              <Input type="number" min="0" max="10" value={priority} onChange={e => setPriority(Number(e.target.value) || 0)} data-testid="chatbot-tech-instr-priority" />
            </div>
          </div>
          <Button onClick={save} disabled={saving || !content.trim()} className="ht-gradient border-0" data-testid="chatbot-tech-instr-save">
            {saving ? "Salvo…" : "Salva istruzione"}
          </Button>
        </div>
      </div>

      <KnowledgeList list={kb} onDelete={onDelete} onToggle={onToggle} onReload={onChange} emptyLabel="Nessuna istruzione salvata" />
    </div>
  );
}

// ============================================================
// TRAINING CHAT TAB — testo + audio (MediaRecorder)
// ============================================================
function TrainingChatTab({ onKbChange }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const bodyRef = useRef(null);

  const loadHistory = () => api.get("/admin/chatbot-tech/training-messages").then(r => setMessages(r.data || []));

  useEffect(() => { loadHistory(); }, []);
  useEffect(() => { bodyRef.current?.scrollTo({ top: 999999, behavior: "smooth" }); }, [messages]);

  const send = async (bodyText, audioFilename) => {
    const text = (bodyText || "").trim();
    if (!text || sending) return;
    setSending(true);
    // Optimistic
    setMessages(prev => [...prev, { _id: `opt-${Date.now()}`, role: "admin", body: text, _local: true }]);
    setInput("");
    try {
      await api.post("/admin/chatbot-tech/training-messages", {
        body: text, audio_filename: audioFilename, save_as_knowledge: true,
      });
      await loadHistory();
      onKbChange && onKbChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setSending(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await transcribeAndSend(blob);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error("Impossibile accedere al microfono. Verifica i permessi del browser.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const transcribeAndSend = async (blob) => {
    setTranscribing(true);
    try {
      const form = new FormData();
      const fn = `voice-${Date.now()}.webm`;
      form.append("file", blob, fn);
      const r = await api.post("/admin/chatbot-tech/transcribe", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const transcribed = (r.data?.text || "").trim();
      if (!transcribed) {
        toast.error("Trascrizione vuota (audio troppo corto?)");
        return;
      }
      // Auto-invia il testo trascritto come messaggio training
      await send(transcribed, fn);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore trascrizione");
    } finally {
      setTranscribing(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[2fr,1fr] gap-6">
      <div className="kpi-card flex flex-col h-[600px]">
        <div className="flex items-center gap-2 pb-3 border-b border-slate-100 mb-3">
          <div className="h-8 w-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center">
            <Robot size={16} weight="fill" />
          </div>
          <div>
            <div className="font-semibold text-sm">Chat di istruzione del bot</div>
            <div className="text-xs text-muted-foreground">Ogni tuo messaggio diventa un'istruzione nella KB. Il bot conferma o chiede chiarimenti.</div>
          </div>
        </div>

        <div ref={bodyRef} className="flex-1 overflow-y-auto space-y-3 pr-1" data-testid="training-chat-body">
          {messages.length === 0 && (
            <div className="text-center text-xs text-slate-500 mt-8 px-4">
              Inizia scrivendo un'istruzione o registrando un vocale. Es. <em>"L'APE non serve per immobili ante 1976 se dichiarati storici."</em>
            </div>
          )}
          {messages.map(m => {
            const isAdmin = m.role === "admin";
            return (
              <div key={m._id || m.id} className={`flex items-start gap-2 ${isAdmin ? "flex-row-reverse" : ""}`}>
                <div className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center ${isAdmin ? "bg-slate-200 text-slate-700" : "bg-purple-100 text-purple-700"}`}>
                  {isAdmin ? <UserIcon size={14} weight="fill" /> : <Robot size={14} weight="fill" />}
                </div>
                <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${isAdmin ? "bg-purple-700 text-white rounded-tr-sm" : "bg-white border border-slate-200 text-slate-800 rounded-tl-sm"}`}>
                  {m.body}
                  {m.audio_filename && <div className="mt-1 text-[10px] opacity-70 flex items-center gap-1"><MicrophoneStage size={10} /> {m.audio_filename}</div>}
                  {m.knowledge_id && !isAdmin && (
                    <div className="mt-1 text-[10px] opacity-70 flex items-center gap-1"><CheckCircle size={10} weight="fill" /> Salvata in KB</div>
                  )}
                </div>
              </div>
            );
          })}
          {(sending || transcribing) && (
            <div className="text-xs text-slate-500 italic ml-9">
              {transcribing ? "Trascrizione audio in corso…" : "Il bot sta rispondendo…"}
            </div>
          )}
        </div>

        <div className="pt-3 border-t border-slate-100">
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }}
              placeholder="Scrivi un'istruzione…"
              rows={2}
              className="flex-1 resize-none border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              data-testid="training-chat-input"
            />
            {!recording ? (
              <Button
                type="button"
                variant="outline"
                onClick={startRecording}
                disabled={sending || transcribing}
                className="h-11 w-11 p-0 rounded-full"
                title="Registra audio"
                data-testid="training-chat-record"
              >
                <MicrophoneStage size={18} weight="fill" />
              </Button>
            ) : (
              <Button
                type="button"
                variant="destructive"
                onClick={stopRecording}
                className="h-11 w-11 p-0 rounded-full animate-pulse"
                title="Stop registrazione"
                data-testid="training-chat-stop"
              >
                <Stop size={18} weight="fill" />
              </Button>
            )}
            <Button
              type="button"
              onClick={() => send(input)}
              disabled={sending || !input.trim()}
              className="h-11 w-11 p-0 rounded-full ht-gradient border-0"
              title="Invia"
              data-testid="training-chat-send"
            >
              <PaperPlaneTilt size={18} weight="fill" />
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="kpi-card">
          <h4 className="font-semibold text-sm mb-2">💡 Come istruire il bot</h4>
          <ul className="text-xs text-slate-600 space-y-1 list-disc list-inside">
            <li>Scrivi UNA regola/normativa per messaggio (es. "Per l'APE gli immobili storici sono esenti…").</li>
            <li>Ogni messaggio diventa un'entry nella KB con priorità 1.</li>
            <li>Puoi usare il microfono per dettare le istruzioni.</li>
            <li>Il bot ti chiederà chiarimenti se un riferimento normativo è ambiguo.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// KB list (shared)
// ============================================================
function KnowledgeList({ list, onDelete, onToggle, onReload, emptyLabel }) {
  const [editing, setEditing] = React.useState(null);
  if (!list || list.length === 0) {
    return (
      <div className="kpi-card text-center py-10 text-slate-500 text-sm">{emptyLabel}</div>
    );
  }
  return (
    <div className="kpi-card overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <th className="pb-2 px-2 py-2">Titolo</th>
            <th className="pb-2 px-2 py-2">Chunks</th>
            <th className="pb-2 px-2 py-2">Priorità</th>
            <th className="pb-2 px-2 py-2">Tag</th>
            <th className="pb-2 px-2 py-2">Attivo</th>
            <th className="pb-2 px-2 py-2">Data</th>
            <th className="pb-2 px-2 py-2 text-right">Azioni</th>
          </tr>
        </thead>
        <tbody>
          {list.map(k => (
            <tr key={k._id || k.id} className="border-b border-slate-50 last:border-0" data-testid={`kb-row-${k._id || k.id}`}>
              <td className="py-2 px-2">
                <div className="font-medium truncate max-w-[300px]">{k.title}</div>
                {k.source_filename && <div className="text-[10px] text-slate-500 truncate max-w-[300px]">{k.source_filename}</div>}
              </td>
              <td className="py-2 px-2">{k.chunks_count ?? "—"}</td>
              <td className="py-2 px-2 flex items-center gap-1"><Star size={12} weight="fill" className="text-amber-500" /> {k.priority || 0}</td>
              <td className="py-2 px-2">
                {(k.tags || []).length === 0 ? (
                  <span className="text-slate-400 text-xs">—</span>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {(k.tags || []).slice(0, 3).map((t, i) => (
                      <span key={`${t}-${i}`} className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{t}</span>
                    ))}
                    {k.tags.length > 3 && (
                      <span className="text-[10px] text-slate-500">+{k.tags.length - 3}</span>
                    )}
                  </div>
                )}
              </td>
              <td className="py-2 px-2">
                <button
                  onClick={() => onToggle(k)}
                  className={`text-xs px-2 py-0.5 rounded-full ${k.is_active ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-500"}`}
                >
                  {k.is_active ? "Sì" : "No"}
                </button>
              </td>
              <td className="py-2 px-2 text-xs text-slate-500">{(k.created_at || "").slice(0, 10)}</td>
              <td className="py-2 px-2 text-right space-x-1">
                <Button size="sm" variant="ghost" onClick={() => setEditing(k)}
                  data-testid={`kb-edit-${k._id || k.id}`}>
                  <PencilSimple size={14} className="text-slate-700" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => onDelete(k._id || k.id)}
                  data-testid={`kb-delete-${k._id || k.id}`}>
                  <Trash size={14} className="text-red-600" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <KbEditDialog
        open={!!editing}
        item={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); onReload?.(); }}
        endpointBase="/admin/chatbot-tech/knowledge"
        testidPrefix="chatbot-tech-kb"
      />
    </div>
  );
}


// ============================================================
// STATS TAB — index BM25 + learning stats aggregate
// ============================================================
function StatsTab() {
  const [indexStats, setIndexStats] = useState(null);
  const [learning, setLearning] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [idx, lrn] = await Promise.all([
        api.get("/admin/chatbot-tech/index-stats"),
        api.get("/admin/chatbot-tech/learning-stats"),
      ]);
      setIndexStats(idx.data);
      setLearning(lrn.data);
    } catch (e) {
      toast.error("Errore nel caricamento statistiche");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totalUploads = learning?.total_events || 0;
  const maxCount = learning?.categories?.[0]?.count || 1;

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="kpi-card" data-testid="stat-kb-chunks">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Knowledge Base</div>
          <div className="mt-1 flex items-baseline gap-2">
            <div className="text-3xl font-bold text-slate-900">{indexStats?.indexed_chunks ?? "—"}</div>
            <div className="text-xs text-slate-500">chunks in memoria</div>
          </div>
          <div className="mt-2 text-xs text-slate-500 flex items-center gap-1">
            <Brain size={12} className="text-purple-700" /> Indice BM25 · TTL {indexStats?.ttl_seconds || 60}s
          </div>
        </div>
        <div className="kpi-card" data-testid="stat-total-uploads">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Documenti caricati</div>
          <div className="mt-1 flex items-baseline gap-2">
            <div className="text-3xl font-bold text-slate-900">{totalUploads}</div>
            <div className="text-xs text-slate-500">ultimi eventi</div>
          </div>
          <div className="mt-2 text-xs text-slate-500 flex items-center gap-1">
            <FileArrowUp size={12} className="text-purple-700" /> Segnali di learning passivi
          </div>
        </div>
        <div className="kpi-card" data-testid="stat-top-category">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Categoria top</div>
          <div className="mt-1 flex items-baseline gap-2">
            <div className="text-2xl font-bold text-slate-900 truncate">
              {learning?.categories?.[0]?.category ?? "—"}
            </div>
          </div>
          <div className="mt-2 text-xs text-slate-500 flex items-center gap-1">
            <ChartBar size={12} className="text-purple-700" /> {learning?.categories?.[0]?.count ?? 0} upload
          </div>
        </div>
      </div>

      {/* Category breakdown */}
      <div className="kpi-card">
        <h3 className="font-semibold text-lg mb-1 flex items-center gap-2">
          <ChartBar size={20} className="text-purple-700" weight="fill" /> Distribuzione categorie documenti
        </h3>
        <p className="text-xs text-muted-foreground mb-4">
          Ordinate per frequenza di upload dai professionisti negli ultimi eventi. Categorie molto
          caricate ma senza copertura Marketplace sono opportunità di prodotto.
        </p>
        {loading && <div className="text-sm text-slate-500">Caricamento…</div>}
        {!loading && (!learning?.categories || learning.categories.length === 0) && (
          <div className="text-sm text-slate-500 italic">
            Nessun evento di upload ancora registrato. I dati appariranno appena i pro caricheranno documenti sulle proprietà.
          </div>
        )}
        {!loading && learning?.categories?.length > 0 && (
          <div className="space-y-2" data-testid="stats-category-list">
            {learning.categories.map((c) => (
              <div key={c.category} className="flex items-center gap-3" data-testid={`stats-cat-${c.category}`}>
                <div className="w-40 text-sm font-medium text-slate-800 truncate">{c.category}</div>
                <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-purple-600 to-purple-800 text-white text-xs font-semibold flex items-center justify-end pr-2"
                    style={{ width: `${Math.max((c.count / maxCount) * 100, 8)}%` }}
                  >
                    {c.count}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Insight box */}
      {!loading && learning?.insight && (
        <div className="rounded-xl border border-purple-200 bg-purple-50 p-4 flex items-start gap-3">
          <Lightbulb size={22} className="text-purple-700 shrink-0 mt-0.5" weight="fill" />
          <div>
            <div className="font-semibold text-sm text-purple-900">Insight</div>
            <div className="text-sm text-purple-800 mt-0.5">{learning.insight}</div>
          </div>
        </div>
      )}

      <Button variant="outline" size="sm" onClick={load} data-testid="stats-refresh">
        Ricarica statistiche
      </Button>
    </div>
  );
}

// ============================================================
// LIMITS TAB — rate limit chatbot: globale + override per utente
// ============================================================
function LimitsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [globalLimit, setGlobalLimit] = useState(15);
  const [savingGlobal, setSavingGlobal] = useState(false);
  // Search + apply override
  const [searchQ, setSearchQ] = useState("");
  const [searchRes, setSearchRes] = useState([]);
  const [applyModal, setApplyModal] = useState(null); // {user}

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/chatbot-limits");
      setData(r.data);
      setGlobalLimit(r.data.global_limit);
    } catch (e) {
      toast.error("Errore caricamento limiti");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const saveGlobal = async () => {
    const n = Number(globalLimit);
    if (!n || n < 1 || n > 1000) { toast.error("Limite valido tra 1 e 1000"); return; }
    setSavingGlobal(true);
    try {
      await api.put("/admin/chatbot-limits/global", { limit: n });
      toast.success(`Limite globale impostato a ${n} messaggi/giorno`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setSavingGlobal(false);
    }
  };

  const doSearch = async (q) => {
    setSearchQ(q);
    if (!q || q.length < 2) { setSearchRes([]); return; }
    try {
      const r = await api.get(`/admin/chatbot-limits/users/search?q=${encodeURIComponent(q)}`);
      setSearchRes(r.data || []);
    } catch { setSearchRes([]); }
  };

  const applyOverride = async (uid, override, unlimited, resetCounter = false) => {
    try {
      await api.put(`/admin/chatbot-limits/user/${uid}`, {
        override: override ?? null, unlimited: !!unlimited, reset_counter: !!resetCounter,
      });
      toast.success("Aggiornato");
      setApplyModal(null);
      setSearchQ("");
      setSearchRes([]);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  if (loading || !data) return <div className="kpi-card">Caricamento…</div>;

  return (
    <div className="space-y-6">
      {/* Global limit setter */}
      <div className="kpi-card">
        <div className="flex items-start gap-3">
          <div className="hidden sm:flex h-11 w-11 rounded-full bg-purple-100 items-center justify-center shrink-0">
            <ShieldCheck size={22} className="text-purple-700" weight="fill" />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-lg font-bold tracking-tight">Limite giornaliero globale</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Numero massimo di messaggi che ogni utente Pro/Operatore può inviare al chatbot in un giorno.
              Reset automatico a mezzanotte (Europe/Rome). Default: {data.default_limit} msg/giorno.
            </p>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <Input
                type="number"
                min="1"
                max="1000"
                value={globalLimit}
                onChange={(e) => setGlobalLimit(e.target.value)}
                className="w-32"
                data-testid="limits-global-input"
              />
              <Button
                onClick={saveGlobal}
                disabled={savingGlobal || Number(globalLimit) === data.global_limit}
                className="ht-gradient border-0"
                data-testid="limits-global-save"
              >
                {savingGlobal ? "Salvo…" : "Salva"}
              </Button>
              <span className="text-xs text-slate-500">
                Attuale: <span className="font-semibold text-slate-800">{data.global_limit}</span> msg/giorno
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Search + apply per-user override */}
      <div className="kpi-card">
        <h3 className="font-semibold text-lg mb-1 flex items-center gap-2">
          <Lightning size={20} className="text-purple-700" weight="fill" /> Sblocca/personalizza un utente
        </h3>
        <p className="text-xs text-muted-foreground mb-3">
          Applica un limite personalizzato o rendi <strong>illimitato</strong> uno specifico utente (es. operatori più attivi).
        </p>
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={searchQ}
            onChange={(e) => doSearch(e.target.value)}
            placeholder="Cerca per email, nome o cognome…"
            className="pl-8"
            data-testid="limits-user-search"
          />
        </div>
        {searchRes.length > 0 && (
          <div className="mt-2 border rounded-lg divide-y max-h-56 overflow-y-auto">
            {searchRes.map((u) => (
              <button
                key={u._id || u.id}
                type="button"
                onClick={() => setApplyModal({ user: u, unlimited: !!u.chat_unlimited, override: u.chat_daily_limit_override ?? "" })}
                className="w-full text-left px-3 py-2 hover:bg-purple-50 flex items-center justify-between gap-2"
                data-testid={`limits-user-${u._id || u.id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{u.first_name} {u.last_name}</div>
                  <div className="text-xs text-slate-500 truncate">{u.email} · {u.role}</div>
                </div>
                <div className="text-xs shrink-0">
                  {u.chat_unlimited ? <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Illimitato</span>
                   : u.chat_daily_limit_override ? <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">Override: {u.chat_daily_limit_override}</span>
                   : <span className="text-slate-400">Globale</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Special users list */}
      {data.special_users.length > 0 && (
        <div className="kpi-card">
          <h3 className="font-semibold text-lg mb-3">Utenti con limite personalizzato ({data.special_users.length})</h3>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500">
              <tr className="border-b border-slate-100">
                <th className="text-left py-2 px-2">Utente</th>
                <th className="text-left py-2 px-2">Ruolo</th>
                <th className="text-left py-2 px-2">Limite</th>
                <th className="text-left py-2 px-2">Uso oggi</th>
                <th className="text-right py-2 px-2">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {data.special_users.map((u) => (
                <tr key={u._id || u.id} className="border-b border-slate-50 last:border-0" data-testid={`limits-special-${u._id || u.id}`}>
                  <td className="py-2 px-2">
                    <div className="font-medium truncate">{u.first_name} {u.last_name}</div>
                    <div className="text-xs text-slate-500 truncate">{u.email}</div>
                  </td>
                  <td className="py-2 px-2 text-xs text-slate-600">{u.role}</td>
                  <td className="py-2 px-2 text-xs">
                    {u.chat_unlimited ? <span className="text-emerald-700 font-semibold">Illimitato</span>
                     : <span className="text-blue-700 font-semibold">{u.chat_daily_limit_override} msg/g</span>}
                  </td>
                  <td className="py-2 px-2 text-xs text-slate-600">{u.daily_chat_count || 0}</td>
                  <td className="py-2 px-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setApplyModal({ user: u, unlimited: !!u.chat_unlimited, override: u.chat_daily_limit_override ?? "" })}>
                      Modifica
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => applyOverride(u._id || u.id, null, false)}>
                      <Trash size={14} className="text-red-600" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Top consumers today */}
      <div className="kpi-card">
        <h3 className="font-semibold text-lg mb-1 flex items-center gap-2">
          <ChartBar size={20} className="text-purple-700" weight="fill" /> Top consumatori oggi
        </h3>
        <p className="text-xs text-muted-foreground mb-3">Utenti che stanno consumando di più oggi ({data.today}).</p>
        {data.top_today.length === 0 ? (
          <div className="text-sm text-slate-500 italic py-2">Nessun utilizzo registrato oggi.</div>
        ) : (
          <div className="space-y-2">
            {data.top_today.map((u) => {
              const limit = u.chat_unlimited ? Infinity : (u.chat_daily_limit_override || data.global_limit);
              const pct = limit === Infinity ? 5 : Math.min(100, (u.daily_chat_count / limit) * 100);
              return (
                <div key={u._id || u.id} className="flex items-center gap-3" data-testid={`limits-top-${u._id || u.id}`}>
                  <div className="w-56 min-w-0">
                    <div className="text-sm font-medium truncate">{u.first_name} {u.last_name}</div>
                    <div className="text-xs text-slate-500 truncate">{u.email} · {u.role}</div>
                  </div>
                  <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
                    <div
                      className={`h-full text-white text-xs font-semibold flex items-center justify-end pr-2 ${pct >= 100 ? "bg-red-500" : pct >= 75 ? "bg-amber-500" : "bg-purple-600"}`}
                      style={{ width: `${Math.max(pct, 8)}%` }}
                    >
                      {u.daily_chat_count}{limit === Infinity ? " (∞)" : `/${limit}`}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Apply modal */}
      {applyModal && (
        <ApplyLimitDialog
          user={applyModal.user}
          initialOverride={applyModal.override}
          initialUnlimited={applyModal.unlimited}
          onClose={() => setApplyModal(null)}
          onApply={(over, unl, reset) => applyOverride(applyModal.user._id || applyModal.user.id, over, unl, reset)}
        />
      )}
    </div>
  );
}


function ApplyLimitDialog({ user, initialOverride, initialUnlimited, onClose, onApply }) {
  const [override, setOverride] = useState(initialOverride === null || initialOverride === undefined ? "" : String(initialOverride));
  const [unlimited, setUnlimited] = useState(!!initialUnlimited);
  const [resetCounter, setResetCounter] = useState(false);
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-md w-full p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-lg font-bold">Configura limite utente</h3>
        <p className="text-sm text-slate-600 mt-0.5">{user.first_name} {user.last_name} · {user.email}</p>
        <div className="mt-4 space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={unlimited} onChange={(e) => setUnlimited(e.target.checked)} data-testid="limits-modal-unlimited" />
            <span>Utente <strong>illimitato</strong> (bypass del rate-limit)</span>
          </label>
          {!unlimited && (
            <div>
              <label className="text-xs font-medium text-slate-600">Override limite (lascia vuoto per usare globale)</label>
              <Input
                type="number"
                min="1"
                max="1000"
                value={override}
                onChange={(e) => setOverride(e.target.value)}
                placeholder="Es. 50"
                data-testid="limits-modal-override"
              />
            </div>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={resetCounter} onChange={(e) => setResetCounter(e.target.checked)} data-testid="limits-modal-reset" />
            <span>Azzera contatore odierno</span>
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button
            className="ht-gradient border-0"
            onClick={() => onApply(override ? Number(override) : null, unlimited, resetCounter)}
            data-testid="limits-modal-apply"
          >
            Applica
          </Button>
        </div>
      </div>
    </div>
  );
}

